import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Menu, X, Home, Building2, Shovel, MapPin, 
  Building, CalendarCheck, Shield, Phone, LogOut, User, ChevronRight, Mail, ExternalLink
} from 'lucide-react';
import { API_URL } from '../config/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dash-18/artifacts/j0uj9jd5_image.png";

const NavLink = ({ to, children, icon: Icon, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-colors duration-200 ${
        isActive 
          ? 'bg-primary text-primary-foreground' 
          : 'text-foreground/80 hover:bg-muted hover:text-primary'
      }`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      <span>{children}</span>
    </Link>
  );
};

const AdBanner = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/bannieres?actives_only=true`);
        if (response.ok) {
          const data = await response.json();
          setBanners(data);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    };
    fetchBanners();
  }, []);

  // Auto-scroll carousel — pauses when user hovers
  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 15000); // 15 seconds per banner

    return () => clearInterval(interval);
  }, [banners.length, isPaused]);

  if (banners.length === 0) return null;

  const banner = banners[currentIndex];

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  return (
    <div
      className="bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-border/50"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="ad-banner"
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="flex gap-3">
            {/* Image */}
            <img 
              src={banner.image} 
              alt={banner.titre}
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-foreground line-clamp-1">{banner.titre}</h4>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{banner.description}</p>
              
              {/* Mobile Contact Links */}
              <div className="flex flex-wrap gap-2 mt-2">
                {banner.numero_telephone && (
                  <a 
                    href={`tel:${banner.numero_telephone}`}
                    className="inline-flex items-center gap-1 text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3 h-3" />
                    Appeler
                  </a>
                )}
                {banner.email && (
                  <a 
                    href={`mailto:${banner.email}`}
                    className="inline-flex items-center gap-1 text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="w-3 h-3" />
                    Email
                  </a>
                )}
                {banner.url_boutique && (
                  <a 
                    href={banner.url_boutique} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Site
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-[auto_1fr_auto] gap-4 items-center">
          {/* Column 1: Photo */}
          <img 
            src={banner.image} 
            alt={banner.titre}
            className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl flex-shrink-0"
          />
          
          {/* Column 2: Title + Description */}
          <div className="min-w-0 flex flex-col justify-center h-full">
            <h4 className="font-semibold text-base sm:text-lg text-foreground line-clamp-1">{banner.titre}</h4>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{banner.description}</p>
          </div>
          
          {/* Column 3: Contact Info */}
          <div className="flex flex-col gap-1.5 text-sm min-w-[200px]">
            {banner.numero_telephone && (
              <a 
                href={`tel:${banner.numero_telephone}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="truncate">{banner.numero_telephone}</span>
              </a>
            )}
            {banner.email && (
              <a 
                href={`mailto:${banner.email}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="truncate">{banner.email}</span>
              </a>
            )}
            {banner.url_boutique && (
              <a 
                href={banner.url_boutique} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="truncate">Visiter le site</span>
              </a>
            )}
          </div>
        </div>
        
        {/* Pagination dots & navigation */}
        {banners.length > 1 && (
          <div className="flex justify-center items-center gap-2 mt-3">
            <button 
              onClick={goToPrev}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
              aria-label="Bannière précédente"
            >
              <ChevronRight className="w-4 h-4 rotate-180 text-primary" />
            </button>
            <div className="flex gap-1.5">
              {banners.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentIndex ? 'bg-primary' : 'bg-primary/30 hover:bg-primary/50'
                  }`}
                  aria-label={`Aller à la bannière ${idx + 1}`}
                />
              ))}
            </div>
            <button 
              onClick={goToNext}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
              aria-label="Bannière suivante"
            >
              <ChevronRight className="w-4 h-4 text-primary" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isAgent } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navItems = [
    { to: '/', label: 'Accueil', icon: Home },
    { to: '/cooperatives', label: 'Coopératives', icon: Building2 },
    { to: '/terrassements', label: 'Terrassements', icon: Shovel },
    { to: '/logements', label: 'Logements', icon: Building },
    { to: '/sites', label: 'Sites', icon: MapPin },
    ...(isAgent() || isAdmin() ? [{ to: '/visites', label: 'Visites', icon: CalendarCheck }] : []),
    ...(isAdmin() ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
    { to: '/contact', label: 'Contact', icon: Phone },
    { to: '/a-propos', label: 'À propos', icon: User },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50" data-testid="header">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="JAMM IMMO" className="h-10 w-auto rounded-lg" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} icon={item.icon}>
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                      <Avatar className="h-10 w-10 border-2 border-primary/20">
                        <AvatarImage src={user.photo_profil} alt={user.prenom} />
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                          {user.prenom?.[0]}{user.nom?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-semibold">{user.prenom} {user.nom}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <div className="flex gap-1 mt-1">
                        {user.roles?.map((role) => (
                          <span 
                            key={role}
                            className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profil')} data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      Mon profil
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                      <LogOut className="w-4 h-4 mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => navigate('/auth')} 
                  className="btn-pill bg-primary hover:bg-primary/90"
                  data-testid="login-button"
                >
                  Connexion
                </Button>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border/50 bg-background" data-testid="mobile-nav">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} icon={item.icon} onClick={closeMobileMenu}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Ad Banner */}
      <AdBanner />

      {/* Main Content */}
      <main className="flex-1" data-testid="main-content">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-2">
              <img src={LOGO_URL} alt="JAMM IMMO" className="h-12 w-auto mb-4 rounded-lg" />
              <p className="text-background/70 text-sm mb-4">
                Votre partenaire de confiance pour l'immobilier au Sénégal. 
                Terrassements, coopératives, terrains et logements - nous vous accompagnons dans tous vos projets.
              </p>
              <div className="flex gap-2">
                <span className="text-background/70 text-sm">+221 77 178 73 79</span>
                <span className="text-background/70 text-sm">|</span>
                <span className="text-background/70 text-sm">+33 623 23 73 18</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-secondary">Navigation</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/cooperatives" className="text-background/70 hover:text-background transition-colors">Coopératives</Link></li>
                <li><Link to="/terrassements" className="text-background/70 hover:text-background transition-colors">Terrassements</Link></li>
                <li><Link to="/sites" className="text-background/70 hover:text-background transition-colors">Sites</Link></li>
                <li><Link to="/logements" className="text-background/70 hover:text-background transition-colors">Logements</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-secondary">Contact</h4>
              <ul className="space-y-2 text-sm text-background/70">
                <li>Dakar, Sénégal</li>
                <li>contact@jammimmo.sn</li>
                <li><Link to="/contact" className="text-secondary hover:underline">Nous contacter</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-background/20 mt-8 pt-8 text-center text-sm text-background/50">
            <p>© {new Date().getFullYear()} JAMM IMMOBILIER. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
